/**
 * 拍照彈窗模組 - CameraModal
 * 
 * 版本：V1.01 | 更新日期：2025-07-16 16:10:00
 * 
 * 檔案功能：提供夥伴拍照留底功能，支援相機權限檢查、即時預覽、拍照、自動儲存
 * 解決問題：讓夥伴可於行事曆登記時拍照留底，並自動儲存到雲端
 * 
 * 更新內容：
 * - V1.01: 初始版本，從 App.js 模組化搬移
 * 
 * 注意事項：
 * - 僅 HTTPS 環境可用，需相機權限
 * - 關連檔案：App.js, firestore, storage
 */

import React, { useState, useEffect } from 'react';

const CameraModal = ({ config, onCapture, onClose }) => {
    const { isOpen, isCapturing, hasPermission, error, stream, bookingInfo } = config;
    const [videoRef, setVideoRef] = useState(null);

    // 當有相機串流時，設定video元素
    useEffect(() => {
        if (stream && videoRef) {
            videoRef.srcObject = stream;
            videoRef.play().catch(err => {
                console.error('播放視頻失敗:', err);
            });
        }
    }, [stream, videoRef]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-[80]" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">📸 拍攝攤位照</h3>
                        {bookingInfo && (
                            <p className="text-sm text-gray-600 mt-1">
                                {bookingInfo.date} - {bookingInfo.marketName}
                            </p>
                        )}
                    </div>
                    <button 
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                        &times;
                    </button>
                </div>

                {error ? (
                    <div className="text-center py-8">
                        <div className="text-red-600 bg-red-50 p-4 rounded-lg mb-4">
                            <div className="text-2xl mb-2">⚠️</div>
                            <p className="font-medium">{error}</p>
                        </div>
                        <button 
                            onClick={onClose}
                            className="bg-gray-200 text-gray-800 font-bold py-2 px-6 rounded-lg"
                        >
                            關閉
                        </button>
                    </div>
                ) : !hasPermission ? (
                    <div className="text-center py-8">
                        <div className="text-blue-600 bg-blue-50 p-4 rounded-lg mb-4">
                            <div className="text-2xl mb-2">🔒</div>
                            <p className="font-medium">需要相機權限</p>
                            <p className="text-sm text-gray-600 mt-1">請允許瀏覽器存取您的相機</p>
                        </div>
                        <button 
                            onClick={onClose}
                            className="bg-gray-200 text-gray-800 font-bold py-2 px-6 rounded-lg"
                        >
                            關閉
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* 相機預覽 */}
                        <div className="relative bg-black rounded-lg overflow-hidden">
                            <video
                                ref={setVideoRef}
                                className="w-full h-64 object-cover"
                                autoPlay
                                playsInline
                                muted
                            />
                            {/* 拍照按鈕 */}
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                                <button
                                    onClick={() => onCapture(bookingInfo)}
                                    disabled={isCapturing}
                                    className={`w-16 h-16 rounded-full border-4 border-white shadow-lg flex items-center justify-center ${
                                        isCapturing 
                                            ? 'bg-gray-400 cursor-not-allowed' 
                                            : 'bg-white hover:bg-gray-100 cursor-pointer'
                                    }`}
                                >
                                    {isCapturing ? (
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
                                    ) : (
                                        <div className="w-8 h-8 bg-red-500 rounded-full"></div>
                                    )}
                                </button>
                            </div>
                        </div>
                        {/* 使用說明 */}
                        <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                            <div className="flex items-center gap-2 mb-1">
                                <span>💡</span>
                                <span className="font-medium">注意事項</span>
                            </div>
                            <ul className="text-xs space-y-1">
                                <li>• 注意腳架是否穩固？</li>
                                <li>• 鞋況保持良好</li>
                            </ul>
                        </div>
                        {/* 操作按鈕 */}
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => onCapture(bookingInfo)}
                                disabled={isCapturing}
                                className={`px-6 py-2 rounded-lg font-bold ${
                                    isCapturing
                                        ? 'bg-gray-400 text-white cursor-not-allowed'
                                        : 'bg-red-500 hover:bg-red-600 text-white'
                                }`}
                            >
                                {isCapturing ? '拍照中...' : '📸 拍照'}
                            </button>
                            <button
                                onClick={onClose}
                                className="px-6 py-2 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300"
                            >
                                取消
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CameraModal; 